import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { alpha, Card, Tab, Tabs, Typography } from '@mui/material';

import { useOrgTenant } from 'src/hooks/auth/use-org-tenant';

import { ListEntityType } from 'src/services/lists/lists.types';

import CandidatesList from 'src/sections/candidates/table/candidates-list';
import ListsTableView from 'src/sections/lists/lists-table-view';

import { TenantType } from 'src/types/enums';

import PlacementsTable from 'src/sections/candidates/placements/placements-table';
import InvitesTable from '../invites/table/invites-table';

enum TabValue {
  CANDIDATES = 'candidates',
  PLACEMENTS = 'placements',
  INVITES = 'invites',
  LISTS = 'lists',
}

export default function CandidatesView() {

  const tenantType = useOrgTenant();

  const [searchParams, setSearchParams] = useSearchParams();

  const [selectedTab, setSelectedTab] = useState<TabValue>();

  const tableTabOptions = useMemo(() => {
    switch (tenantType) {
      case TenantType.Client:
        return [TabValue.CANDIDATES, TabValue.PLACEMENTS, TabValue.LISTS];
      case TenantType.Recruiter:
        return [TabValue.CANDIDATES, TabValue.PLACEMENTS, TabValue.INVITES, TabValue.LISTS];
      default:
        return [];
    }
  }, [tenantType]);

  // Set default tab
  useEffect(() => {
    // eslint-disable-next-line no-useless-return
    if (selectedTab) return;

    let defaultTab: TabValue = TabValue.CANDIDATES

    const existingTab = searchParams.get('tab');
    if (existingTab && Object.values(TabValue).includes(existingTab as TabValue)) {
      // Check if the existing tab is in the available options
      if (tableTabOptions.includes(existingTab as TabValue)) {
        defaultTab = existingTab as TabValue;
      }
    }

    setSelectedTab(defaultTab);

  }, [selectedTab, searchParams, setSearchParams, tableTabOptions]);

  useEffect(() => {
    // eslint-disable-next-line no-useless-return
    if (!selectedTab) return;

    setSearchParams({ "tab": selectedTab }, { replace: true })

  }, [selectedTab, setSearchParams]);

  return (
    <Card>
      <Tabs
        value={selectedTab}
        onChange={(_event, tab) => setSelectedTab(tab)}
        sx={{
          px: 2.5,
          boxShadow: (theme) => `inset 0 -2px 0 0 ${alpha(theme.palette.grey[500], 0.08)}`,
        }}
      >
        {tableTabOptions.map((tab) => (
          <Tab
            key={tab}
            iconPosition="end"
            value={tab}
            label={
              <Typography
                variant="subtitle2"
                sx={{ textTransform: 'capitalize', fontWeight: 'bold' }}
              >
                {tab}
              </Typography>
            }
          />
        ))}
      </Tabs>

      {
        selectedTab === TabValue.CANDIDATES &&
        <CandidatesList />
      }

      {
        selectedTab === TabValue.PLACEMENTS &&
        <PlacementsTable />
      }

      {
        selectedTab === TabValue.INVITES &&
        <InvitesTable />
      }

      {
        selectedTab === TabValue.LISTS &&
        <ListsTableView
          entityType={ListEntityType.CANDIDATE}
        />
      }

    </Card>
  );
}
